// `contact.logged_in.v1` — emitted by contacts-db-backend off the
// donor-activity-events DynamoDB stream (PE-1853) when a donor completes a
// Clerk OTP login at the Contact Portal (embedded widget or standalone
// portal). It seeds the "Logged In" marketing segment and a future
// PocketFlows re-engagement path.
//
// IT IS A PER-LOGIN EVENT, NOT A STATE EVENT. One event fires per genuine
// login session; `originIdempotencyKey` is the session-scoped key
// `{contactId}#{clerkSessionId}` (Decision 4 / R6) so the Inngest bridge
// (dedup on `id: originIdempotencyKey`) collapses retries of the SAME login
// but never collapses two distinct logins (which would undercount). It carries
// `isFirstLogin` + `daysSinceLastLogin` + `source` so PocketFlows can later
// filter to "notable" logins WITHOUT a recapture; the bridge forwards all now.
//
// Producer: contacts-db-backend (`Source: 'charitystack.contacts-db'`), the
//   donor-activity-events stream handler — emission home is contacts-db because
//   it already owns the `charitystack-<stage>` bus wiring, `EVENT_BUS_NAME`,
//   and `events:PutEvents` IAM (edit-donor-details, which owns the write path,
//   emits nothing today). Best-effort emit (NEVER raises). The handler SKIPS
//   `origin: "backfill"` rows, so a one-time Clerk backfill does not trigger a
//   re-engagement storm. `organizationId` is the contact row's `merchantID`,
//   which IS the CS org id in the marketing domain.
// Consumer: marketing-backend — the logged-in bridge (marketing gate:
//   non-marketing orgs dropped) → the `logged-in` Inngest workflow.
// detailType in EventBridge: `contact.logged_in.v1` (Commitment 6).
//
// Status: active

import { z } from 'zod';

import { eventEnvelopeSchema } from './base.js';

// Where the login happened. A server-validated hint derived by the Contact
// Portal from its host (`host.mode === 'page'` ⇒ standalone portal, else the
// embedded widget). `unknown` is a reserved defensive value — currently
// unproduced: the Clerk backfill attributes pre-launch history to
// portal_page (product decision 2026-08-01: the widget is new, the
// portal was the only pre-launch surface), and backfill rows never emit
// this event anyway.
export const contactLoggedInSourceSchema = z.enum([
  'portal_widget',
  'portal_page',
  'unknown',
]);

export type ContactLoggedInSource = z.infer<typeof contactLoggedInSourceSchema>;

export const contactLoggedInV1Schema = eventEnvelopeSchema.extend({
  // The contactsdb contact who logged in. Marketing resolves their
  // pocketflow_customer_id from it; a contact that was never synced to PF is a
  // clean skip in the workflow.
  contactId: z.string().min(1).max(256),
  // Server-validated capture source (never trusted from the client as truth).
  source: contactLoggedInSourceSchema,
  // True when this is the donor's first-ever login (the signup). Derived
  // server-side (first activity event / no prior `last_login`) — never taken
  // from the request body.
  isFirstLogin: z.boolean(),
  // Whole days since the donor's previous login, or null on the first login.
  // Lets PocketFlows filter to "notable" (e.g. first-in-N-days) logins later
  // without a recapture.
  daysSinceLastLogin: z.number().int().nonnegative().nullable(),
});

export type ContactLoggedInV1 = z.infer<typeof contactLoggedInV1Schema>;

export const CONTACT_LOGGED_IN_V1_DETAIL_TYPE =
  'contact.logged_in.v1' as const;
